import { Box, BoxProps, FlexProps, UseBreakpointOptions } from '@chakra-ui/react'
import { useFirstMountState } from '@react-hookz/web'
import {
  calculateBreakpointForWidth,
  containerBreakpointValue,
  getBreakpointValue,
  type BreakpointName,
} from '@src/components/sco/utilities/BreakpointProvider/helpers'
import { useAppContextStore } from '@src/contexts/AppContextStore'
import { useWindow } from '@src/hooks/useWindow'
import { IS_SSR, IS_SSR_ENABLED } from '@src/lib/server-side-rendering'
import { useDebounce, useMeasure } from '@uidotdev/usehooks'
import React, { createContext, FC, useCallback, useContext, useMemo, useRef } from 'react'
import { ReactNode } from 'react'

export type { BreakpointName } from '@src/components/sco/utilities/BreakpointProvider/helpers'

type BreakpointContextValue = {
  activeContainerBreakpoint: BreakpointName
  activeContainerWidth: number
  getBreakpointValue: (values: any, arg?: any) => any
  isMobile: boolean
}

export const BreakpointContext = createContext<BreakpointContextValue>({
  activeContainerBreakpoint: 'base',
  activeContainerWidth: 0,
  getBreakpointValue: () => null,
  isMobile: false,
})

export const useBreakpointContext = () => useContext(BreakpointContext)

export function useContainerBreakpointValue<T = any>(values: Partial<Record<string, T>> | Array<T | null>) {
  if (!values) {
    return values
  }
  return containerBreakpointValue(values)
}

export function useContainerBreakpoint(arg?: string | UseBreakpointOptions): string {
  const { activeContainerBreakpoint } = useBreakpointContext()
  return activeContainerBreakpoint || (typeof arg === 'string' ? arg : arg.fallback)
}

export const BreakpointContextProvider: FC<{
  boxProps?: BoxProps & FlexProps
  initialWidth?: number
  userAgent?: string
  userAgentWidth?: number
  children?: ReactNode
}> = props => {
  const window = useWindow({
    initialWidth: props.initialWidth,
    userAgent: props.userAgent,
  })
  const { contextStore } = useAppContextStore()
  const isFirstRender = useFirstMountState()
  const [boxRef, { width: boxRefWidth }] = useMeasure()

  let width = 0
  if (IS_SSR) {
    width = props.initialWidth || props.userAgentWidth || window.ssrInnerWidth || window.innerWidth
  } else {
    if (isFirstRender) {
      width = props.initialWidth || props.userAgentWidth || contextStore.get('ssrWidthFromUserAgent') || 0
    } else {
      width = boxRefWidth || window.innerWidth || 0
    }
  }

  const debouncedWidth = useDebounce(width, 10)
  const widthForBreakpoint = isFirstRender ? width : (debouncedWidth ?? width)
  const previousBreakpointRef = useRef<BreakpointName | null>(null)

  const activeContainerBreakpoint = useMemo(() => {
    const next = calculateBreakpointForWidth(widthForBreakpoint, previousBreakpointRef.current)
    previousBreakpointRef.current = next
    return next
  }, [widthForBreakpoint])

  const getBreakpointValueFn = useCallback(
    (values?: any, args?: { ssrValue?: any }) => {
      if ((IS_SSR || (IS_SSR_ENABLED && isFirstRender)) && args?.ssrValue !== undefined) {
        return args.ssrValue
      }
      return getBreakpointValue(activeContainerBreakpoint, values)
    },
    [activeContainerBreakpoint, isFirstRender]
  )

  const value = useMemo(() => {
    return {
      activeContainerBreakpoint,
      activeContainerWidth: widthForBreakpoint,
      getBreakpointValue: getBreakpointValueFn,
      isMobile: getBreakpointValueFn([true, true, false, false, false]),
    }
  }, [activeContainerBreakpoint, getBreakpointValueFn, widthForBreakpoint])

  return (
    <BreakpointContext.Provider value={value}>
      <Box {...(props.boxProps || {})} ref={boxRef}>
        {props.children}
      </Box>
    </BreakpointContext.Provider>
  )
}
